import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteChangeReason, AutocompleteInputChangeReason, Tooltip } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import InfiniteAutocomplete, { InfiniteAutocompleteProps } from './InfiniteAutocomplete';
import {
    AxiosInfinitePageableResponse,
    PAGEABLE_SIZE,
    PageableRequest,
    PageableResponse,
} from '../types/AxiosPageableResponse';
import Utils from './Utils';
import { AgencyMarketRegionCriteria } from '../types/Criteria/AgencyMarketRegionCriteria';
import { useStateTransitions } from '../hooks/Transition';

type EmptyTransitionState = 'empty' | 'not-empty';

type AgencyMarketRegion = string;

const getOptionKey = (option: AgencyMarketRegion | any): string | number => {
    return String(option);
};

const getOptionLabel = (option: AgencyMarketRegion | any): string => {
    return option || '';
};

const defaultPageable: PageableRequest = {
    page: 1,
    size: PAGEABLE_SIZE,
    sort: 'asc',
    sortBy: 'name',
};

const fetch = async (
    criteria: AgencyMarketRegionCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<PageableResponse<AgencyMarketRegion>> => {
    const source = axios.CancelToken.source();

    if (signal) {
        if (signal.aborted) source.cancel('aborted');
        signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
    }

    const response = await axios.post(
        '/api/dealers/agencyMarketRegions',
        {
            ...criteria,
            ids: criteria?.ids || undefined,
            agencyIds: criteria?.agencyIds || undefined,
            advertiserIds: criteria?.advertiserIds || undefined,
        },
        {
            headers: { XBusy: 'false' },
            params: {
                ...pageable,
                page: page - 1,
            },
            cancelToken: source.token,
        }
    );
    return response.data;
};

const fetchInitial = async (
    ids: string[] | string | null | undefined,
    criteria: AgencyMarketRegionCriteria,
    signal?: AbortSignal
): Promise<any> => {
    const _ids: string[] = Array.isArray(ids) ? ids : ids != null ? [ids] : [];

    if (_ids.length === 0) {
        return [];
    }

    const { content } = await fetch({ ...criteria, ids: _ids }, { page: 1, size: 1000 }, signal);

    return content;
};

const fetchInfinite = async (
    criteria: AgencyMarketRegionCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<AxiosInfinitePageableResponse<AgencyMarketRegion>> => {
    const { content } = await fetch(criteria, { page, ...pageable }, signal);

    return {
        items: content,
        nextPage: content.length === pageable.size ? page + 1 : undefined,
    };
};

const fetchMass = async (criteria: AgencyMarketRegionCriteria, signal?: AbortSignal): Promise<any> => {
    const { content } = await fetch(criteria, { page: 1, size: 1000 }, signal);

    return content;
};

interface AgencyMarketRegionCheckboxAutoCompleteProps<
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
> extends Omit<
        InfiniteAutocompleteProps<AgencyMarketRegion, Multiple, DisableClearable, FreeSolo>,
        'options' | 'value' | 'getOptionKey' | 'getOptionLabel'
    > {
    onLoading?: () => void;
    onEmpty?: () => void;
    onNotEmpty?: () => void;
    criteria?: AgencyMarketRegionCriteria;
    value: Multiple extends true ? string[] : string | null;
    deferChangeUntilClose?: boolean;
}

const AgencyMarketRegionCheckboxAutoComplete = <
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>({
    onLoading = () => {},
    onEmpty = () => {},
    onNotEmpty = () => {},
    onChange,
    disabled = false,
    deferChangeUntilClose = false,
    ...props
}: AgencyMarketRegionCheckboxAutoCompleteProps<Multiple, DisableClearable, FreeSolo>) => {
    const { debounce } = Utils;

    const [value, setValue] = useState<
        InfiniteAutocompleteProps<AgencyMarketRegion, Multiple, DisableClearable, FreeSolo>['value']
    >(
        (props.multiple ? [] : null) as InfiniteAutocompleteProps<
            AgencyMarketRegion,
            Multiple,
            DisableClearable,
            FreeSolo
        >['value']
    );
    const [inputValue, setInputValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isMassSelected, setIsMassSelected] = useState<boolean>(false);
    const pendingValueRef = useRef<
        InfiniteAutocompleteProps<AgencyMarketRegion, Multiple, DisableClearable, FreeSolo>['value'] | null
    >(null);
    const hasChangedWhileOpenRef = useRef<boolean>(false);

    const criteriaWithoutName = useMemo((): AgencyMarketRegionCriteria => {
        return {
            ...props?.criteria,
            ids: props?.criteria?.ids || [],
            agencyIds: props?.criteria?.agencyIds || [],
            advertiserIds: props?.criteria?.advertiserIds || [],
        };
    }, [props.criteria]);

    const criteria = useMemo((): AgencyMarketRegionCriteria => {
        return {
            ...criteriaWithoutName,
            name: inputValue,
        };
    }, [criteriaWithoutName, inputValue]);

    // Fetch initial value if IDs are provided
    const { data: initialValue, isLoading: isLoadingInitialValue } = useQuery({
        queryKey: ['infinite/agencyMarketRegions/initial', props.value, criteriaWithoutName],
        queryFn: ({ signal }) => fetchInitial(props.value, criteria, signal),
        enabled: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (initialValue) {
            if (props.multiple) {
                setValue((initialValue ?? []) as any);
            } else {
                setValue((initialValue[0] ?? null) as any);
            }
        }
    }, [initialValue, props.multiple]);

    const { data, fetchNextPage, hasNextPage, isFetched, isFetching, isFetchingNextPage, refetch } = useInfiniteQuery({
        queryKey: ['infinite/agencyMarketRegions', criteria],
        queryFn: ({ pageParam, signal }) =>
            fetchInfinite(criteria, { page: pageParam || 1, size: defaultPageable.size }, signal),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 0,
        // staleTime: 1000 * 60 * 5,
        enabled: true,
        refetchOnWindowFocus: false,
    });

    const options = useMemo<AgencyMarketRegion[]>(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

    const { refetch: refetchMass, isLoading: isLoadingMass } = useQuery({
        queryKey: ['infinite/agencyMarketRegions/mass', criteria],
        queryFn: ({ signal }) => fetchMass(criteria, signal),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const handleOnClose = useCallback((): void => {
        setIsOpen(false);
        if (deferChangeUntilClose && hasChangedWhileOpenRef.current && pendingValueRef.current !== null) {
            onChange(pendingValueRef.current as any);
        }
        hasChangedWhileOpenRef.current = false;
        pendingValueRef.current = null;
    }, [deferChangeUntilClose, onChange]);

    const handleOnChange = useCallback(
        (
            newValue:
                | InfiniteAutocompleteProps<AgencyMarketRegion, Multiple, DisableClearable, FreeSolo>['value']
                | undefined,
            reason?: AutocompleteChangeReason
        ): void => {
            setValue(newValue);
            if (deferChangeUntilClose) {
                pendingValueRef.current = newValue ?? null;
                hasChangedWhileOpenRef.current = true;
            } else {
                onChange(newValue as any);
            }

            // If cleared via X button, trigger close logic immediately
            if (reason === 'clear') {
                handleOnClose();
            }
        },
        [deferChangeUntilClose, handleOnClose, onChange]
    );

    const debouncedOnInputChange = useRef(
        debounce((newInputValue: string): void => {
            refetch();
        }, 1000)
    ).current;

    const isLoading = useMemo((): boolean => {
        return isFetching || isFetchingNextPage || isLoadingInitialValue || isLoadingMass;
    }, [isFetching, isFetchingNextPage, isLoadingInitialValue, isLoadingMass]);

    const isSearching = inputValue.trim().length > 0;
    const isDisabled = disabled || (!isLoading && options.length === 0 && !isSearching);

    useStateTransitions<EmptyTransitionState>({
        conditions: {
            empty: () => isFetched && !isLoading && !isSearching && data !== undefined && options.length === 0,
            'not-empty': () => isFetched && !isLoading && !isSearching && data !== undefined && options.length > 0,
        },
        callbacks: {
            empty: () => onEmpty?.(),
            'not-empty': () => onNotEmpty?.(),
        },
    });

    const handleOnInputChange = (
        event: React.SyntheticEvent,
        newInputValue: string,
        reason: AutocompleteInputChangeReason
    ): void => {
        setInputValue(newInputValue);
        debouncedOnInputChange(newInputValue);
    };

    const handleOnScroll = useCallback((): void => {
        if (!isMassSelected) {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isMassSelected]);

    useEffect((): void => {
        if (isLoading === true) {
            onLoading();
        }
    }, [isLoading, onLoading]);

    useEffect(() => {
        if (isOpen === false) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        setIsMassSelected(false);
    }, [criteria]);

    const handleOnFetchMass = async (): Promise<ReadonlyArray<AgencyMarketRegion>> => {
        const { data } = await refetchMass();

        setIsMassSelected(true);

        return data;
    };

    return (
        <Tooltip
            arrow
            disableHoverListener={!isDisabled}
            title="The Market/Region filter is disabled because there are no Market/Regions matching the current filter selections."
        >
            <InfiniteAutocomplete
                disableCloseOnSelect={props.multiple}
                openOnFocus={props.multiple}
                selectOnFocus={props.multiple}
                blurOnSelect={false}
                clearOnBlur={false}
                label="Market/Region"
                {...props}
                disabled={isDisabled}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                options={options}
                value={value}
                loading={isLoading}
                onChange={handleOnChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                onScroll={handleOnScroll}
                onFetchMass={handleOnFetchMass}
                onOpen={(): void => setIsOpen(true)}
                onClose={handleOnClose}
            />
        </Tooltip>
    );
};

export type { AgencyMarketRegionCheckboxAutoCompleteProps };
export default AgencyMarketRegionCheckboxAutoComplete;
